/* The multi-level menu.
 *
 * Two shapes of the same Navigation tree, which Shopify gives us three levels
 * deep as link.links.links:
 *
 *   Desktop — a full-width panel under the masthead, opened by hover and by
 *   keyboard focus, holding the children as columns with the grandchildren
 *   listed beneath each.
 *
 *   Phone — the menu drawer becomes a drill-down: levels are siblings in a
 *   stack, the one on screen sits at translateX(0), the one behind it slides
 *   left, the one coming in slides from the right.
 *
 * Every level is rendered as real markup with real hrefs and carries
 * `hidden`. The script clears `hidden` on start and owns visibility from then
 * on, because `hidden` cannot be transitioned.
 */
(function () {
  'use strict';

  /* --- Desktop: the mega panels ------------------------------------------ */
  (function () {
    var items = [].slice.call(document.querySelectorAll('[data-mega-item]'));
    if (!items.length) return;

    var open = null;
    var timer = null;        /* the close delay */
    var waiting = null;      /* the open delay — see showSoon */

    /* How long the pointer must rest on an item before its panel opens. A
       nav is something people sweep across on the way to somewhere else, and
       without this every item it passes flashes a 200px panel open and shut.
       Max asked for it after noticing revolve.com wait; 300ms is long enough
       to reject a sweep and short enough not to feel broken.

       It applies ONLY to the first open. Once a panel is up the reader is in
       the menu and moving along it, so item to item switches at once —
       waiting again there would feel like the menu was sticking. */
    var OPEN_DELAY = 300;

    function panel(item) { return item.querySelector('.jw-mega'); }
    function trigger(item) { return item.querySelector('[data-mega-open]'); }

    function show(item) {
      window.clearTimeout(timer);
      if (open === item) return;
      if (open) hide(open);
      open = item;
      trigger(item).setAttribute('aria-expanded', 'true');
      panel(item).classList.add('jw-mega--on');
    }

    /* Rest, then open — unless a panel is already open, in which case this is
       a move within the menu and it happens immediately. */
    function showSoon(item) {
      window.clearTimeout(waiting);
      if (open) { show(item); return; }
      waiting = window.setTimeout(function () { show(item); }, OPEN_DELAY);
    }

    function hide(item) {
      if (!item) return;
      trigger(item).setAttribute('aria-expanded', 'false');
      panel(item).classList.remove('jw-mega--on');
      if (open === item) open = null;
    }

    /* A short delay, because the mouse leaves the link before it reaches the
       panel and a diagonal path crosses neither. */
    function hideSoon() {
      window.clearTimeout(timer);
      timer = window.setTimeout(function () { hide(open); }, 160);
    }

    items.forEach(function (item) {
      panel(item).hidden = false;

      /* Ask the POINTER what it is, never the media query. `(hover: none)`
         describes the device's PRIMARY input, so a touchscreen laptop reports
         it true with a mouse plugged in and gating hover on it kills the
         panel outright — which is exactly what happened on Max's machine.
         A pointerenter that says 'mouse' is a mouse, whatever the device also
         has.

         It settles the touch case in the same move. A tap fires pointerenter
         too, and opening on that would let the click straight through to the
         link; ignoring every non-mouse pointerenter leaves the tap to the
         click handler below, where the first one opens and the second
         follows. */
      item.addEventListener('pointerenter', function (e) {
        if (e.pointerType === 'mouse') showSoon(item);
      });
      item.addEventListener('pointerleave', function (e) {
        if (e.pointerType !== 'mouse') return;
        /* Leaving before the rest is up cancels it, which is the whole
           point: a pointer passing through never opens anything. */
        window.clearTimeout(waiting);
        hideSoon();
      });
      /* A browser with no PointerEvent at all still gets hover. */
      if (!window.PointerEvent) {
        item.addEventListener('mouseenter', function () { showSoon(item); });
        item.addEventListener('mouseleave', function () { window.clearTimeout(waiting); hideSoon(); });
      }

      /* Focus and tap open at ONCE. Both are deliberate acts — nobody tabs
         to an item or puts a finger on it by accident — and a delay on
         either would read as the menu failing to respond. */
      item.addEventListener('focusin', function () { window.clearTimeout(waiting); show(item); });
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) hideSoon();
      });
      /* Whether THIS tap is the one that opens the panel, decided at
         pointerdown — the only moment that still knows. By the time the click
         arrives the panel is already open, because a tap focuses the link and
         focusin opens it, so comparing against `open` in the click handler
         says "already open, let it through" and the first tap navigates. */
      var opensIt = false;
      item.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse') opensIt = open !== item;
      });
      trigger(item).addEventListener('click', function (e) {
        var mouse = !e.pointerType || e.pointerType === 'mouse';
        /* A panel the width hides (the sections menu on a phone) has
           nothing to show, so the tap is just a link. */
        var shown = getComputedStyle(panel(item)).display !== 'none';
        if (!mouse && opensIt && shown) { e.preventDefault(); window.clearTimeout(waiting); show(item); opensIt = false; }
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !open) return;
      var back = trigger(open);
      hide(open);
      back.focus();
    });

    document.addEventListener('click', function (e) {
      if (open && !open.contains(e.target)) hide(open);
    });
  })();

  /* --- Phone drawer: the drill-down -------------------------------------- */
  (function () {
    var stack = document.querySelector('[data-menu-stack]');
    if (!stack) return;

    var levels = [].slice.call(stack.querySelectorAll('.jw-menu__level'));
    var root = stack.querySelector('[data-level-root]');
    if (!root) return;

    /* Which levels are behind the one on screen, outermost first. */
    var trail = [root];

    /* Two steps, and the order between them is the whole trick — both halves
       of it learned by watching focus land on the drawer's mark instead of in
       the level that just arrived:
         paint() frees the level we are moving TO, because .focus() cannot
         enter a subtree that is still inert from the last seal;
         seal() shuts the others AFTER focus has moved, because sealing the
         level we came from while the button just clicked still holds focus
         makes the browser evict that focus itself. */
    function paint() {
      var here = trail[trail.length - 1];
      levels.forEach(function (level) {
        var on = level === here;
        level.classList.toggle('jw-menu__level--on', on);
        level.classList.toggle('jw-menu__level--back', trail.indexOf(level) > -1 && !on);
      });
      here.inert = false;
      stack.dataset.depth = String(trail.length - 1);
    }

    /* inert, not just off-screen: a level that has slid away is still in the
       layout, and without this Tab walks into links nobody can see. It also
       takes the level out of the accessibility tree, which a transform does
       not. */
    function seal() {
      var here = trail[trail.length - 1];
      levels.forEach(function (level) { if (level !== here) level.inert = true; });
    }

    function push(id) {
      var next = document.getElementById(id);
      if (!next || trail.indexOf(next) > -1) return;
      trail.push(next);
      paint();
      focusFirst(next);
      seal();
    }

    function pop() {
      if (trail.length < 2) return;
      var leaving = trail.pop();
      paint();
      /* Back to the row that opened it, so a keyboard does not start over. */
      var opener = stack.querySelector('[data-menu-push="' + leaving.id + '"]');
      if (opener) opener.focus({ preventScroll: true });
      seal();
    }

    /* preventScroll is not optional here. The level being focused is still
       off to the right when focus enters it, and the browser's default is to
       scroll it into view — which it does by scrolling the STACK, the nearest
       clipping ancestor. Measured without it: the levels ended up 281px out
       and the drill-down drifted further sideways with every step. */
    function focusFirst(level) {
      var first = level.querySelector('[data-menu-pop], a, button');
      if (first) first.focus({ preventScroll: true });
    }

    /* The accordions: the third level, opening in place inside a level rather
       than as a slide of its own. `hidden` is their no-script state too, so
       the script takes it off and holds them shut with `inert` instead —
       which a transformed-away or zero-height element is not, and Tab would
       otherwise walk straight into a closed one. */
    var subs = [].slice.call(stack.querySelectorAll('.jw-menu__sub'));

    function toggle(trigger) {
      var sub = document.getElementById(trigger.getAttribute('data-menu-toggle'));
      if (!sub) return;
      var isOpen = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!isOpen));
      sub.classList.toggle('is-open', !isOpen);
      sub.inert = isOpen;
    }

    function collapseAll() {
      stack.querySelectorAll('[data-menu-toggle]').forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
      subs.forEach(function (sub) { sub.classList.remove('is-open'); sub.inert = true; });
    }

    levels.forEach(function (level) { level.hidden = false; });
    subs.forEach(function (sub) { sub.hidden = false; sub.inert = true; });
    paint();
    seal();

    stack.addEventListener('click', function (e) {
      /* Both of these are links carrying a real href — preventDefault is what
         turns following one into opening the other. */
      var push_ = e.target.closest('[data-menu-push]');
      if (push_) { e.preventDefault(); push(push_.getAttribute('data-menu-push')); return; }
      var toggle_ = e.target.closest('[data-menu-toggle]');
      if (toggle_) { e.preventDefault(); toggle(toggle_); return; }
      if (e.target.closest('[data-menu-pop]')) { e.preventDefault(); pop(); }
    });

    /* A tap that came before this script ran was held by theme.liquid;
       now it can do what it meant. */
    window.jwMenuReady = true;
    var held = window.jwMenuPending;
    window.jwMenuPending = null;
    if (held) held.click();

    /* Escape steps back one level; only at the root does it close the drawer.
       This has to be `cancel`, not `keydown`: a dialog's Escape fires cancel,
       and preventing THAT is what stops it closing. Preventing the keydown
       does nothing — measured, the drawer shut from two levels down. */
    stack.closest('dialog').addEventListener('cancel', function (e) {
      if (trail.length > 1) { e.preventDefault(); pop(); }
    });

    /* Closing the drawer returns it to the top, so it does not reopen three
       levels deep with no memory of how it got there. */
    stack.closest('dialog').addEventListener('close', function () {
      trail = [root];
      paint();
      seal();
      collapseAll();
    });
  })();

  /* --- Search from app.20v.us -------------------------------------------
     The stories (Max, 2026-10-18) and the products (2026-10-31) both come
     from the store app, which keeps each brand's to its own domain and
     matches the words typed exactly; Shopify's search did neither. The
     endpoint is the form's data-stories or the page's data-search.
     Resolves to null if the app does not answer. */
  function ask(endpoint, q, extra, signal) {
    var brand = document.documentElement.getAttribute('data-brand') || '';
    return fetch(endpoint + '?brand=' + encodeURIComponent(brand) + '&q=' + encodeURIComponent(q) + extra, { signal: signal })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function (e) { if (e.name === 'AbortError') throw e; return null; });
  }
  function stories(endpoint, q, limit, signal) { return ask(endpoint, q, '&limit=' + limit, signal); }
  function products(endpoint, q, offset, limit, signal) {
    return ask(endpoint, q, '&type=product&offset=' + offset + '&limit=' + limit, signal)
      .then(function (found) { return found && found.results ? found : (offset ? null : suggest(q, signal)); });
  }

  /* If the app does not answer, Shopify's own suggestions stand in, quietly
     (Max, 2026-10-31: a shopper is never shown an error they can do nothing
     about): ten at most, loosely matched, kept to this brand by its tag. */
  function suggest(q, signal) {
    var tag = { july: 'July', west: 'West' }[document.documentElement.getAttribute('data-brand')];
    return fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=10', { signal: signal })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var list = ((j && j.resources.results.products) || []).filter(function (p) { return !tag || p.tags.indexOf(tag) >= 0; });
        return { count: list.length, results: list.map(function (p) {
          return { title: p.title, url: p.url.split('?')[0], kicker: p.type, price: '$' + Number(p.price).toFixed(2),
                   available: p.available, image: p.image || '', w: 0, h: 0 };
        }) };
      })
      .catch(function (e) { if (e.name === 'AbortError') throw e; return null; });
  }

  /* A Shopify CDN picture at a width. */
  function sized(url, w) { return url + (url.indexOf('?') < 0 ? '?' : '&') + 'width=' + w; }

  function esc(t) {
    return String(t).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; });
  }

  /* The products as rows under a search field: picture, type and price,
     title (was sections/predictive-search.liquid). */
  function productRows(found) {
    if (!found || !found.results.length) return '';
    var n = found.count;
    return '<p class="jw-results__count">' + n + (n === 1 ? ' product' : ' products') + '</p><ul class="jw-results">' +
      found.results.map(function (r) {
        return '<li><a class="jw-result" href="' + esc(r.url) + '"><span class="jw-result__media">' +
          (r.image ? '<img src="' + esc(sized(r.image, 120)) + '" alt="" width="60" height="80" loading="lazy">' : '') +
          '</span><span class="jw-result__text"><span class="jw-result__kicker">' + (r.kicker ? '<span>' + esc(r.kicker) + '</span>' : '') +
          '<span class="jw-result__price">' + (r.available ? esc(r.price) : 'Sold out') + '</span></span>' +
          '<span class="jw-result__name">' + esc(r.title) + '</span></span></a></li>';
      }).join('') + '</ul>';
  }

  /* The products as cards, as snippets/product-card.liquid draws them. */
  function productCards(found) {
    return found.results.map(function (r) {
      return '<a class="jw-product" href="' + esc(r.url) + '"><div class="jw-product__media">' +
        (r.image ? '<img src="' + esc(sized(r.image, 600)) + '" srcset="' + esc(sized(r.image, 300)) + ' 300w, ' +
          esc(sized(r.image, 520)) + ' 520w, ' + esc(sized(r.image, 780)) + ' 780w" sizes="(max-width: 720px) 45vw, 280px" alt=""' +
          (r.w ? ' width="' + r.w + '" height="' + r.h + '"' : '') + ' loading="lazy">' : '') +
        '</div><h3 class="jw-product__name">' + esc(r.title) + '</h3><span class="jw-product__price">' +
        (r.available ? esc(r.price) : 'Sold out') + '</span></a>';
    }).join('');
  }

  /* The stories as the products' rows: picture, section, title, and the
     count over them in the same voice as the products'. */
  function storyRows(found) {
    if (!found || !found.results.length) return '';
    var n = found.count;
    return '<p class="jw-results__count">' + n + (n === 1 ? ' story' : ' stories') + '</p><ul class="jw-results">' +
      found.results.map(function (r) {
        return '<li><a class="jw-result" href="' + esc(r.url) + '"><span class="jw-result__media">' +
          (r.image ? '<img src="' + esc(r.image) + '" alt="" width="70" height="70" loading="lazy">' : '') +
          '</span><span class="jw-result__text"><span class="jw-result__kicker">' + esc(r.section) + '</span>' +
          '<span class="jw-result__name">' + esc(r.title) + '</span></span></a></li>';
      }).join('') + '</ul>';
  }

  /* --- Results as you type ---------------------------------------------
     Ten products and six stories from the store app (Max, 2026-09-27; the
     products from the app since 2026-10-31), in the phone menu and the
     desktop dialog alike. The results go in the element the field's
     aria-controls names; in the menu they take the stack's place while there
     is a query. Enter still submits the form to the full results page. */
  function predictive(form) {
    var input = form.querySelector('input[name="q"]');
    var x = form.querySelector('.jw-menu__search-clear');
    var out = document.getElementById(input.getAttribute('aria-controls'));
    var stack = form.closest('.jw-menu') && document.querySelector('[data-menu-stack]');
    var timer = null, pending = null, lastQ = '';

    function show(q) {
      out.hidden = !q;
      if (stack) stack.hidden = !!q;
      if (!q) { out.innerHTML = ''; lastQ = ''; }
    }

    function run() {
      var q = input.value.trim();
      if (q === lastQ) return;
      lastQ = q;
      if (pending) pending.abort();
      if (!q) { show(''); return; }
      pending = new AbortController();
      Promise.all([
        products(form.dataset.stories, q, 0, 10, pending.signal),
        stories(form.dataset.stories, q, 6, pending.signal)
      ])
        .then(function (both) {
          if (input.value.trim() !== q) return;   /* typed on since */
          var html = productRows(both[0]) + storyRows(both[1]);
          out.innerHTML = html
            ? html + '<a class="jw-results__all" href="' + form.action + '?q=' + encodeURIComponent(q) +
                '">All results for “' + esc(q) + '” &rarr;</a>'
            : '<p class="jw-empty">Nothing matched “' + esc(q) + '”.</p>';
          show(q);
        })
        .catch(function () {});
    }

    function clear() { input.value = ''; x.hidden = true; run(); }

    x.addEventListener('click', function () { clear(); input.focus(); });
    input.addEventListener('input', function () {
      x.hidden = !input.value;
      clearTimeout(timer);
      timer = setTimeout(run, 200);
    });
    /* Escape clears a query before it closes whatever holds the field. */
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && input.value) { e.preventDefault(); e.stopPropagation(); clear(); }
    });
    return { input: input, clear: clear };
  }

  /* The results page: the products as cards, 40 at a time with More for
     the next 40, then the stories; "Nothing matched" only if neither found
     any. */
  (function () {
    var box = document.querySelector('[data-search-results]');
    if (!box || !window.fetch) return;
    var q = box.getAttribute('data-q'), url = box.getAttribute('data-search'), PAGE = 40;
    Promise.all([products(url, q, 0, PAGE), stories(url, q, 50)]).then(function (both) {
      var p = both[0], s = both[1];
      var html = '';
      if (p && p.results.length) {
        html += '<p class="jw-results__count">' + p.count + (p.count === 1 ? ' product' : ' products') + '</p>' +
          '<div class="jw-product-grid" data-search-grid>' + productCards(p) + '</div>' +
          (p.count > PAGE ? '<button class="jw-waterfall__more" type="button" data-search-more>More</button>' : '');
      }
      html += s && s.results.length ? '<div class="jw-search-page__stories">' + storyRows(s) + '</div>' : '';
      box.innerHTML = html || '<p class="jw-empty">Nothing matched “' + esc(q) + '”.</p>';

      var more = box.querySelector('[data-search-more]'), shown = p ? p.results.length : 0;
      if (more) more.addEventListener('click', function () {
        more.setAttribute('aria-busy', 'true');
        products(url, q, shown, PAGE).then(function (next) {
          more.removeAttribute('aria-busy');
          if (!next) return;
          box.querySelector('[data-search-grid]').insertAdjacentHTML('beforeend', productCards(next));
          shown += next.results.length;
          if (shown >= next.count || !next.results.length) more.remove();
        });
      });
    });
  })();

  (function () {
    if (!window.fetch) return;
    var forms = document.querySelectorAll('[data-predictive]');
    forms.forEach(function (form) {
      var field = predictive(form);
      /* A closed menu or dialog reopens empty, not on an old search. */
      var dlg = form.closest('dialog');
      if (dlg) dlg.addEventListener('close', field.clear);
    });
  })();
})();
